import React, { type ReactNode } from "react";
import { cn } from "@/utils/cn";
import { PANELS, PANEL_GROUPS, type PanelId } from "./panels";
import { ReportSlotContext } from "./reportSlot";

interface ReportRailProps {
  /** A panel by id; the rail puts it in a folded slot, or an inline one when expanded. */
  renderPanel: (id: PanelId) => ReactNode;
  /** Panels shown on the canvas: ringed. */
  active: ReadonlyArray<PanelId>;
  /** Panels in the layout that this width does not show: outlined dashed. */
  hidden?: ReadonlyArray<PanelId>;
  /** Phone: panels open in place, as tall as their content, folded again from their header. */
  expanded?: ReadonlyArray<PanelId>;
  onFold?: (id: PanelId) => void;
  /** Panels left out, and a group left with none drops its heading. */
  omit?: ReadonlyArray<PanelId>;
  /** The panel waiting for an edge on the canvas. */
  placing: PanelId | null;
  /** The panel just refused, and why, shown under it. */
  refused: { panel: PanelId; reason: string } | null;
  onPick: (id: PanelId) => void;
  /** A press that may become a drag onto the canvas. */
  onPressStart?: (id: PanelId, event: React.PointerEvent) => void;
  className?: string;
}

/** Every Reporting panel, grouped by source: the left rail on a tablet and
 *  a desktop, the whole page on a phone. Folded, a panel is a two-line item
 *  that opens on a pick; on a phone an open panel expands in place. */
export const ReportRail: React.FC<ReportRailProps> = ({
  renderPanel,
  active,
  hidden = [],
  expanded = [],
  onFold,
  omit = [],
  placing,
  refused,
  onPick,
  onPressStart,
  className,
}) => (
  <nav aria-label="Report panels" className={cn("@container space-y-4", className)}>
    {PANEL_GROUPS.map((group) => {
      const panels = PANELS.filter((p) => p.group === group.id && !omit.includes(p.id));
      if (panels.length === 0) return null;
      return (
        <section key={group.id} aria-label={group.label ?? undefined}>
          {group.label && (
            <h2 className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">{group.label}</h2>
          )}
          <ul className="space-y-1.5">
            {panels.map(({ id }) => (
              <li key={id} data-panel-item={id}>
                {expanded.includes(id) ? (
                  <ReportSlotContext.Provider value={{ kind: "inline", onFold: () => onFold?.(id) }}>
                    {renderPanel(id)}
                  </ReportSlotContext.Provider>
                ) : (
                  <div
                    data-rail-item={id}
                    className={cn(
                      "rounded-2xl cursor-pointer select-none transition-shadow hover:shadow-sm",
                      active.includes(id) && "ring-2 ring-blue-500",
                      hidden.includes(id) && "outline-2 outline-dashed outline-gray-300",
                      placing === id && "ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-100"
                    )}
                    // The header is the panel's own button and opens it itself; the rest of the card opens it here.
                    onClick={(e) => {
                      if (e.target instanceof Element && e.target.closest("button")) return;
                      onPick(id);
                    }}
                    onPointerDown={(e) => onPressStart?.(id, e)}
                  >
                    <ReportSlotContext.Provider value={{ kind: "rail", onOpen: () => onPick(id) }}>
                      {renderPanel(id)}
                    </ReportSlotContext.Provider>
                  </div>
                )}
                {refused?.panel === id && (
                  <p role="status" className="px-2 pt-1.5 pb-1 text-[12px] leading-4 text-amber-700">
                    {refused.reason}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      );
    })}
  </nav>
);
